import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { OrbitingIcon } from "@/components/OrbitingIcon";
import { PackageCheck, FileText, BarChart, CheckCircle, Sparkles as LucideSparkles, Zap, Quote, Banknote, Boxes, LayoutDashboard } from "lucide-react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Helmet } from 'react-helmet-async';
import ReferralLandingModal from '@/components/ReferralLandingModal';
import { supabase } from '@/supabase/client';
import { SettlementDemo } from '@/components/demo/SettlementDemo';
import { ExcelDemo } from '@/components/demo/ExcelDemo';

// Testimonial data
interface Testimonial {
  imageSrc: string;
  alt: string;
  quote: string;
  source: string;
  variant?: 'landscape' | 'portrait';
}

const testimonials: Testimonial[] = [
  {
    imageSrc: "/assets/testimonial1.png",
    alt: "사용자 후기 1",
    quote: "정산서 만드는 수고로움을 덜 수 있어서 많이 편해졌어요",
    source: "슈***님",
    variant: 'landscape',
  },
  {
    imageSrc: "/assets/testimonial2.png",
    alt: "사용자 후기 2",
    quote: "셀러헬퍼 최고예요!! 빠른 답변도 너무 감사해요",
    source: "딸***님",
    variant: 'landscape',
  },
  {
    imageSrc: "/assets/testimonial3.png",
    alt: "사용자 후기 3",
    quote: "택배 엑셀 정리가 진짜 편해요. 복붙으로 분류해주고 저장까지",
    source: "다***님",
    variant: 'portrait',
  },
  {
    imageSrc: "/assets/testimonial4.png",
    alt: "사용자 후기 4",
    quote: "매번 복사 붙여넣기하고 무료배송 체크하던 일을 안 해도 되니 너무 좋습니다",
    source: "빈****님",
    variant: 'landscape',
  },
  {
    imageSrc: "/assets/testimonial5.png",
    alt: "사용자 후기 5",
    quote: "매출 대비 수수료 모델이 아니라 정액제여서 너무 만족하면서 쓰고 있어요",
    source: "정**님",
    variant: 'landscape',
  },
  {
    imageSrc: "/assets/testimonial6.png",
    alt: "사용자 후기 6",
    quote: "유용한 기능이 많아서 매일매일 편리하게 쓰고 있어요",
    source: "이***님",
    variant: 'portrait',
  },
  {
    imageSrc: "/assets/testimonial7.png",
    alt: "사용자 후기 7",
    quote: "정산서 셀러헬퍼 쓰면서 너무 편해졌어요. 라방 셀러들한테 정말 편하네요",
    source: "블***님",
    variant: 'portrait',
  },
  {
    imageSrc: "/assets/testimonial8.png",
    alt: "사용자 후기 8",
    quote: "정산서 작성 시간이 엄청 줄었고, 배송정보 복붙으로 배송엑셀까지 완성되니 택배 작업하기에도 엄청 편해요",
    source: "굿***님",
    variant: 'portrait',
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

function TestimonialCard({ testimonial }: { testimonial: Testimonial }) {
  return (
    <motion.div
      variants={cardVariants}
      className="break-inside-avoid mb-6 bg-white rounded-xl ring-1 ring-gray-200 shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden flex flex-col"
    >
      {/* Quote */}
      <div className="px-5 pt-5 pb-3">
        <Quote className="w-5 h-5 text-brand-primary/40 mb-2" />
        <p className="text-sm md:text-base font-medium text-brand-dark leading-relaxed">
          "{testimonial.quote}"
        </p>
        <p className="mt-2 text-xs text-gray-400">— {testimonial.source}</p>
      </div>
      {/* Image */}
      <div className="w-full bg-gray-50 flex items-center justify-center px-4 pb-4">
        <img
          src={testimonial.imageSrc}
          alt={testimonial.alt}
          loading="lazy"
          className="w-full h-auto max-h-[240px] md:max-h-[280px] object-contain rounded-lg"
        />
      </div>
    </motion.div>
  );
}

// FAQ data
const faqItems = [
  {
    question: "정말 무료로 체험할 수 있나요?",
    answer: "네! 회원가입 즉시 7일간 프리미엄 플랜을 무료로 체험하실 수 있습니다.\n별도의 문의나 카드 등록 없이 바로 시작할 수 있습니다."
  },
  {
    question: "무료 체험이 끝나면 어떻게 되나요?",
    answer: "체험 기간이 끝나도 자동으로 결제되지 않습니다.\n셀러헬퍼가 내 업무에 도움이 된다고 느끼시면, 원하는 플랜을 선택하고 카드를 등록해서 구독하시면 됩니다.\n충분히 경험해보시고, 가치가 있다고 판단될 때만 결정하세요."
  },
  {
    question: "회원가입은 어떻게 하나요?",
    answer: "구글 또는 카카오 계정으로 클릭 한 번이면 가입이 완료됩니다.\n별도의 이메일 인증이나 복잡한 절차 없이 바로 시작할 수 있습니다."
  },
  {
    question: "주문 정보는 안전하게 보관되나요?",
    answer: "셀러헬퍼에 입력된 모든 주문 정보는 암호화되어 안전하게 보관됩니다.\n고객 이름, 연락처, 주소 등 민감한 정보는 철저히 보호되며, 외부에 절대 공유되지 않습니다."
  },
  {
    question: "결제는 안전한가요?",
    answer: "셀러헬퍼는 국내 대표 결제 인프라인 토스페이먼츠를 통해 결제를 처리합니다.\n토스페이먼츠는 금융감독원 감독을 받는 전자금융거래 서비스로, 24시간 이상거래 탐지 시스템(FDS)과 국제 보안 표준(ISO/IEC 27701) 인증을 갖추고 있습니다."
  },
  {
    question: "입금매칭은 아이폰에서도 사용할 수 있나요?",
    answer: "네, 안드로이드와 아이폰 모두 사용할 수 있습니다."
  },
];

// 추가 기능 카드 (입금매칭·재고관리·주문/고객 관리)
const moreFeatures = [
  {
    icon: Banknote,
    title: "입금매칭",
    lines: [
      "계좌를 한 번 연동하면 입금 문자가 올 때마다 주문과 자동으로 매칭됩니다.",
      "클릭 한 번으로 정산서 '입금 완료' 처리까지 이어집니다.",
      "추천된 주문이 맞지 않으면 다른 주문을 직접 골라 연결할 수 있습니다.",
    ],
  },
  {
    icon: Boxes,
    title: "재고관리",
    lines: [
      "주문이 입금 완료되면 재고가 자동으로 차감되고, 입금 전 상태로 되돌리면 자동으로 복원됩니다.",
      "엑셀로 상품을 한 번에 등록할 수 있습니다.",
      "입고·출고·주문 차감 내역을 확인할 수 있습니다.",
    ],
  },
  {
    icon: LayoutDashboard,
    title: "주문·고객 관리",
    lines: [
      "닉네임이나 상품명으로 주문을 바로 찾습니다.",
      "고객별 월 구매 총액을 확인할 수 있습니다.",
      "거래 파기 고객을 등록해두면 다음 주문 때 경고로 알려줍니다.",
    ],
  },
];

export default function LandingPage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [totalOrders, setTotalOrders] = useState<number | null>(null);

  // Fetch real-time total order count
  useEffect(() => {
    supabase.rpc('get_total_order_count').then(({ data, error }) => {
      if (!error && data != null) setTotalOrders(data);
    });
  }, []);

  useEffect(() => {
    const code = searchParams.get('code');
    if (code) {
      const upperCode = code.toUpperCase();
      sessionStorage.setItem('referralCode', upperCode);
      setReferralCode(upperCode);
    }
  }, [searchParams]);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.substring(1);
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 0);
    }
  }, [location.hash]);

  return (
    <div className="flex flex-col min-h-screen bg-white font-sans">
      <Helmet>
        <title>셀러헬퍼: 틱톡·유튜브·인스타그램 라방 셀러 필수 웹사이트</title>
        <meta name="description" content="틱톡·유튜브·인스타그램 라방 셀러를 위한 정산서·주문 송장 엑셀 자동화, 입금매칭, 재고관리, 합배/킵 판단, 매출 분석 기능 제공" />
        <link rel="canonical" href="https://sellerhelper.kr/" />
        <meta property="og:title" content="셀러헬퍼: 틱톡·유튜브·인스타그램 라방 셀러 필수 웹사이트" />
        <meta property="og:description" content="틱톡·유튜브·인스타그램 라방 셀러를 위한 정산서·주문 송장 엑셀 자동화, 입금매칭, 재고관리, 합배/킵 판단, 매출 분석 기능 제공" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sellerhelper.kr/" />
        <meta property="og:image" content="https://sellerhelper.kr/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="셀러헬퍼: 틱톡·유튜브·인스타그램 라방 셀러 필수 웹사이트" />
        <meta name="twitter:description" content="틱톡·유튜브·인스타그램 라방 셀러를 위한 정산서·주문 송장 엑셀 자동화, 입금매칭, 재고관리, 합배/킵 판단, 매출 분석 기능 제공" />
        <meta name="twitter:image" content="https://sellerhelper.kr/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({ "@context": "https://schema.org", "@type": "Organization", "name": "셀러헬퍼", "url": "https://sellerhelper.kr/", "logo": "https://sellerhelper.kr/og-image.png" })}</script>
        <script type="application/ld+json">{JSON.stringify({ "@context": "https://schema.org", "@type": "WebSite", "url": "https://sellerhelper.kr/", "potentialAction": { "@type": "SearchAction", "target": "https://sellerhelper.kr/?q={search_term_string}", "query-input": "required name=search_term_string" } })}</script>
        {/* Step 6: ld+json price fix 4900 → 9800 */}
        <script type="application/ld+json">{JSON.stringify({ "@context": "https://schema.org", "@type": "SoftwareApplication", "name": "셀러헬퍼", "applicationCategory": "BusinessApplication", "operatingSystem": "Web", "offers": { "@type": "Offer", "price": "9800", "priceCurrency": "KRW" } })}</script>
      </Helmet>

      {/* ===== HEADER ===== */}
      <header className="sticky top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg shadow-sm">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center">
              <Link to="/" className="text-2xl font-bold text-brand-dark">
                SellerHelper
              </Link>
            </div>
            {/* Desktop nav */}
            <nav className="hidden md:flex items-center space-x-6">
              <Link to="#features" className="text-sm font-medium text-gray-600 hover:text-brand-primary transition-colors whitespace-nowrap">
                주요 기능
              </Link>
              <Link to="#reviews" className="text-sm font-medium text-gray-600 hover:text-brand-primary transition-colors whitespace-nowrap">
                사용 후기
              </Link>
              <Link to="#pricing" className="text-sm font-medium text-gray-600 hover:text-brand-primary transition-colors whitespace-nowrap">
                가격
              </Link>
              {/* Step 2: CTA unified to "무료로 시작하기" */}
              <Button asChild className="bg-brand-primary text-white hover:opacity-80 transition-opacity">
                <Link to="/order">무료로 시작하기</Link>
              </Button>
            </nav>
            {/* Step 1: Mobile nav with CTA button */}
            <div className="md:hidden flex items-center gap-2">
              <nav className="flex items-center space-x-3 overflow-x-auto px-1 py-2 scroll-smooth">
                <Link to="#features" className="text-xs font-medium text-gray-600 hover:text-brand-primary transition-colors whitespace-nowrap">
                  주요 기능
                </Link>
                <Link to="#reviews" className="text-xs font-medium text-gray-600 hover:text-brand-primary transition-colors whitespace-nowrap">
                  후기
                </Link>
                <Link to="#pricing" className="text-xs font-medium text-gray-600 hover:text-brand-primary transition-colors whitespace-nowrap">
                  가격
                </Link>
              </nav>
              {/* Step 1: Mobile CTA button */}
              <Button asChild size="sm" className="bg-brand-primary text-white hover:opacity-80 transition-opacity text-xs px-3 py-1 h-8 flex-shrink-0">
                <Link to="/order">무료로 시작하기</Link>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-grow">
        {/* ===== HERO SECTION ===== */}
        <section className="relative w-full flex items-center justify-center overflow-hidden min-h-[calc(100vh-4rem)] py-12 md:py-20">
          <div className="container relative z-10 flex flex-col justify-center items-center h-full px-4 sm:px-6 lg:px-8">
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-800 mb-6 md:mb-10 leading-relaxed text-center max-w-3xl mx-auto">
              <span className="tiktok-dual-text">틱톡</span>, <span className="text-platform-youtube">유튜브</span>, <span className="instagram-gradient-text">인스타</span> 라방 셀러님들!
              <br />
              {"주문 금액 계산, 무료 배송 체크, 배송 엑셀 수작업..."}
              <br />
              {"단순 반복 작업으로 고통받고 있지 않으신가요?"}
            </p>
            {/* ===== STATS BAR (inside hero) ===== */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-10 md:gap-16 mb-8 md:mb-14">
              <div className="text-center">
                <p className="text-xl md:text-2xl font-extrabold text-gray-900">
                  {totalOrders != null ? totalOrders.toLocaleString() + '건' : 'Loading...'}
                </p>
                <p className="text-xs text-gray-500 mt-1">현재 실시간 누적 처리 주문 수</p>
              </div>
              <div className="hidden sm:block w-px h-8 bg-gray-300" />
              <div className="text-center">
                <p className="text-xl md:text-2xl font-extrabold text-gray-900">7일</p>
                <p className="text-xs text-gray-500 mt-1">프리미엄 무료 체험</p>
              </div>
              <div className="hidden sm:block w-px h-8 bg-gray-300" />
              <div className="text-center">
                <p className="text-xl md:text-2xl font-extrabold text-gray-900">95%</p>
                <p className="text-xs text-gray-500 mt-1">구독 유지율</p>
              </div>
              <div className="hidden sm:block w-px h-8 bg-gray-300" />
              <div className="text-center">
                <p className="text-xl md:text-2xl font-extrabold text-gray-900">1분</p>
                <p className="text-xs text-gray-500 mt-1">가입부터 사용까지</p>
              </div>
            </div>
            {/* Mobile: rounded card, Desktop: circle */}
            <div className="relative w-full max-w-xl md:max-w-2xl rounded-2xl md:aspect-square md:rounded-full flex flex-col items-center justify-center text-center p-6 sm:p-8 md:p-3 bg-white border border-brand-primary min-h-0 hero-glow-effect">
              <h1 className="font-bold text-brand-dark text-xl sm:text-2xl md:text-4xl lg:text-5xl">
                <span className="block mb-1 sm:mb-2 md:mb-4 text-brand-primary">정산서</span>
                <span className="block mb-1 sm:mb-2 md:mb-4 text-brand-primary">주문 송장 엑셀 정리</span>
                <span className="block">반복 업무, 셀러헬퍼가 해결합니다</span>
              </h1>
              <p className="mt-3 sm:mt-5 md:mt-6 font-semibold text-brand-primary text-sm sm:text-base md:text-xl">
                하루 주문 100건 넘는 셀러들은 이미 셀러헬퍼로 넘어오고 있습니다.
              </p>
              <p className="mt-3 sm:mt-6 md:mt-8 text-sm sm:text-sm md:text-lg text-[#4B5563] max-w-xs sm:max-w-md md:max-w-xl">
                계산기 없이도 정산서가 자동으로 완성됩니다.<br className="hidden sm:block" />
                택배사 업로드용 엑셀을 한줄한줄 일일이 정리하지 않아도 됩니다.
                <br className="hidden sm:block" />
                클릭 몇 번이면 끝납니다.
              </p>
              {/* Step 2: Hero primary CTA → Link to /order */}
              <Button
                asChild
                className="mt-4 sm:mt-6 md:mt-8 px-6 py-3 text-sm sm:px-6 sm:py-3 sm:text-base md:text-lg text-white bg-brand-primary hover:opacity-80 transition-opacity shadow-lg"
              >
                <Link to="/order">무료로 시작하기</Link>
              </Button>
              {/* Step 2: Secondary scroll link */}
              <button
                onClick={() => document.getElementById('feature-demo')?.scrollIntoView({ behavior: 'smooth' })}
                className="mt-3 text-xs sm:text-sm text-gray-500 hover:text-brand-primary transition-colors underline underline-offset-2"
              >
                주요 기능 먼저 체험하기 ↓
              </button>
              {/* Orbiting icons - desktop only */}
              <div className="absolute inset-0 w-full h-full hidden md:block pointer-events-none">
                <div className="absolute top-1/2 left-1/2 w-0 h-0">
                  <OrbitingIcon icon={<PackageCheck className="text-purple-600 w-7 h-7" />} delay={0} initialAngle={0} />
                  <OrbitingIcon icon={<FileText className="text-brand-secondary w-7 h-7" />} delay={0} initialAngle={120} />
                  <OrbitingIcon icon={<BarChart className="text-brand-primary w-7 h-7" />} delay={0} initialAngle={240} />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== FEATURES SECTION ===== */}
        <section id="features" className="w-full py-16 md:py-24 bg-brand-light">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-brand-dark">주요 기능 체험하기</h2>
              <p className="mt-3 text-lg text-gray-600 max-w-2xl mx-auto">
                백문이 불여일타! <strong className="font-bold text-brand-primary">직접 눌러보며</strong> 경험해보세요.<br />
                회원가입 즉시 <strong className="font-bold text-brand-primary">7일간 프리미엄 플랜 무료 체험</strong>이 시작됩니다.
              </p>
            </div>

            <div className="space-y-16 md:space-y-24">
              {/* Feature 1: 정산서 자동 완성 */}
              <div id="feature-demo" className="flex flex-col gap-8">
                <div className="text-center md:text-left md:flex md:items-center md:justify-between mb-4">
                  <div>
                    <div className="inline-flex p-3 bg-brand-primary/10 rounded-full mb-4">
                      <FileText className="w-8 h-8 text-brand-primary" />
                    </div>
                    <h3 className="text-2xl md:text-3xl font-semibold text-brand-dark mb-3">
                      정산서 자동 완성 & 원클릭 복사
                    </h3>
                    <p className="text-gray-600 text-base md:text-lg max-w-xl">
                      상품을 클릭만 하세요. 계산은 셀러헬퍼가 합니다.<br />
                      완성된 정산서를 복사해서 바로 채팅방에 붙여넣기만 하면 끝!<br />
                      자주 쓰는 배송비와 제주/산간 배송비를 저장해두고 바로 적용합니다.
                    </p>
                  </div>
                </div>
                <div className="w-full">
                  <SettlementDemo />
                </div>
              </div>

              {/* Feature 2: 배송 정보 자동 정리 */}
              <div className="flex flex-col gap-8">
                <div className="text-center md:text-left md:flex md:items-center md:justify-between mb-4">
                  <div>
                    <div className="inline-flex p-3 bg-brand-secondary/10 rounded-full mb-4">
                      <PackageCheck className="w-8 h-8 text-brand-secondary" />
                    </div>
                    <h3 className="text-2xl md:text-3xl font-semibold text-brand-dark mb-3">
                      배송 정보 자동 정리 & 엑셀 다운로드
                    </h3>
                    <p className="text-gray-600 text-base md:text-lg max-w-xl">
                      "주소요~" 하고 보내준 정신없는 메시지들.<br />
                      복사해서 붙여넣기만 하면, 이름/전화번호/주소를 자동으로 분류해줍니다.<br />
                      택배사 양식에 맞춰 내려받고, 정산서 순서로 정렬할 수 있습니다.
                    </p>
                  </div>
                </div>
                <div className="w-full">
                  <ExcelDemo />
                </div>
              </div>

              {/* Feature 3: 매출 분석 */}
              <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
                <div className="md:w-1/2 text-center md:text-left">
                  <div className="inline-flex p-3 bg-brand-primary/10 rounded-full mb-4">
                    <BarChart className="w-8 h-8 text-brand-primary" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-semibold text-brand-dark mb-3">매출 현황 분석</h3>
                  <p className="text-gray-600 text-base md:text-lg mb-6">
                    매출 달력으로 판매 성과를 한눈에 확인할 수 있습니다.<br />
                    엑셀 쓸 필요 없이, 자동으로 매출이 기록됩니다.<br />
                    결제수단 기록을 사용하면 일별 매출을 현금과 카드로 나눠 확인할 수 있습니다.
                  </p>
                </div>
                <div className="md:w-1/2">
                  <img
                    src="/assets/sales.png"
                    alt="매출 분석 기능"
                    className="rounded-lg shadow-xl w-full aspect-video object-contain bg-white"
                  />
                </div>
              </div>

              {/* Feature 4: 입금매칭 · 재고관리 · 주문/고객 관리 */}
              <div className="flex flex-col gap-8">
                <h3 className="text-2xl md:text-3xl font-semibold text-brand-dark text-center md:text-left break-keep">
                  정산부터 주문·고객 관리까지 한 곳에서
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {moreFeatures.map(({ icon: Icon, title, lines }) => (
                    <div key={title} className="bg-white rounded-xl ring-1 ring-gray-200 shadow-md p-6">
                      <div className="inline-flex p-3 bg-brand-primary/10 rounded-full mb-4">
                        <Icon className="w-6 h-6 text-brand-primary" />
                      </div>
                      <h4 className="text-lg font-semibold text-brand-dark mb-3">{title}</h4>
                      <ul className="space-y-2 break-keep">
                        {lines.map((line) => (
                          <li key={line} className="flex items-start text-sm text-gray-600 leading-relaxed">
                            <CheckCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0 text-brand-primary" />
                            {line}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Step 5: Mid-section CTA after features */}
            <div className="mt-16 md:mt-24 text-center">
              <Button
                asChild
                size="lg"
                className="px-8 py-6 text-base sm:text-lg font-bold bg-brand-primary text-white hover:opacity-90 transition-opacity shadow-lg rounded-full"
              >
                <Link to="/order">이제 내 상품으로 해보기</Link>
              </Button>
              <p className="mt-3 text-sm text-gray-500">
                회원가입 즉시 7일간 프리미엄 무료 체험
              </p>
            </div>
          </div>
        </section>

        {/* Step 4: YouTube "사용 방법" section REMOVED */}

        {/* ===== REVIEWS SECTION ===== */}
        <section id="reviews" className="w-full py-16 md:py-24 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-brand-dark">사용 후기</h2>
            <p className="mt-3 text-center text-gray-600 max-w-xl mx-auto">
              실제 라방 셀러분들의 생생한 후기를 확인해보세요.
            </p>
            <motion.div
              className="mt-12 columns-1 md:columns-2 lg:columns-3 gap-6 max-w-5xl mx-auto"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              transition={{ staggerChildren: 0.15 }}
            >
              {testimonials.map((item, idx) => (
                <TestimonialCard key={idx} testimonial={item} />
              ))}
            </motion.div>
          </div>
        </section>

        {/* ===== PRICING SECTION ===== */}
        <section id="pricing" className="w-full py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10 md:mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-brand-dark mb-2">
                가격
              </h2>
              {/* Step 6: Free trial subtitle */}
              <p className="mt-3 text-lg text-gray-600 max-w-2xl mx-auto">
                회원가입 즉시 <strong className="font-bold text-brand-primary">7일간 프리미엄 플랜 무료 체험</strong>
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch max-w-4xl mx-auto">
              {plansData.map((plan) => (
                <PlanCard key={plan.id} plan={plan} />
              ))}
            </div>
          </div>
        </section>

        {/* ===== FAQ SECTION (Step 8) ===== */}
        <section id="faq" className="w-full py-16 md:py-24 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-brand-dark mb-10">
              자주 묻는 질문
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {faqItems.map((item, idx) => (
                <AccordionItem key={idx} value={`faq-${idx}`}>
                  <AccordionTrigger className="text-left text-base md:text-lg font-medium text-brand-dark hover:no-underline">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-500 text-sm md:text-base leading-relaxed pl-4 border-l-2 border-brand-primary/20 ml-1">
                    {item.answer.split('\n').map((line, i, arr) => (
                      <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
                    ))}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* ===== CTA FOOTER ===== */}
        <section className="w-full py-20 md:py-32 bg-gradient-to-b from-white via-brand-light to-brand-primary/10 relative overflow-hidden">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <div className="mb-8 space-y-2">
              <h2 className="text-3xl md:text-5xl font-extrabold text-brand-dark tracking-tight">
                <span className="tiktok-dual-text">틱톡</span>
                <span className="text-brand-dark mx-2">·</span>
                <span className="text-platform-youtube">유튜브</span>
                <span className="text-brand-dark mx-2">·</span>
                <span className="instagram-gradient-text">인스타그램</span>
              </h2>
              <h2 className="text-3xl md:text-5xl font-extrabold text-brand-dark mt-4">
                라방 셀러들의 성공,
              </h2>
              <h2 className="text-3xl md:text-5xl font-extrabold text-brand-primary mt-2">
                셀러헬퍼가 함께합니다.
              </h2>
            </div>

            <p className="text-lg md:text-xl text-gray-600 mb-10 max-w-2xl mx-auto font-light leading-relaxed">
              반복되는 정산과 엑셀 업무에서 벗어나,<br />
              온전히 <span className="text-brand-dark font-semibold">판매와 소통</span>에만 <span className="text-brand-dark font-semibold">집중</span>하세요.
            </p>

            <Button
              asChild
              size="lg"
              className="px-8 py-6 text-lg sm:text-xl font-bold bg-brand-primary text-white hover:opacity-90 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1 rounded-full"
            >
              <Link to="/order">무료로 시작하기</Link>
            </Button>

            <p className="mt-6 text-sm font-medium text-brand-primary">
              회원가입 즉시 7일간 프리미엄 플랜 무료 체험
            </p>
            <p className="mt-1 text-xs text-gray-400">
              별도 문의나 카드 등록 없이 바로 시작할 수 있습니다.
            </p>
          </div>
        </section>

      </main>

      <ReferralLandingModal referralCode={referralCode} />
    </div>
  )
}

// Subscription Plan interface
interface Plan {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  discountRate?: number;
  description: string;
  features: string[];
  recommended?: boolean;
  bgColorClass: string;
  borderColorClass: string;
  textColorClass: string;
  badgeBgColorClass: string;
  icon?: React.ReactNode;
}

const getPlanFeatures = (planId: string): string[] => {
  switch (planId) {
    case 'standard':
      return [
        '정산서 복사 기능 월 150회',
        '배송 엑셀 자동 정리 기능 월 150회',
        '배송 엑셀 다운로드 기능 무제한',
        '입금매칭 기능 제공',
        '재고관리 기능 제공',
        '거파 고객 등록 기능 제공',
        '일별·월별 매출 기능 제공',
        '고객별 월 구매 총액 기능 제공'
      ];
    case 'premium':
      return [
        '스탠다드 플랜의 모든 기능',
        '정산서 복사 기능 무제한',
        '배송 엑셀 자동 정리 기능 무제한',
        '단골 고객 순위 기능 제공',
        '많이 주문된 상품 월별 상위 10개 기능 제공'
      ];
    default:
      return [];
  }
};

const plansData: Plan[] = [
  {
    id: 'standard',
    name: '스탠다드 플랜',
    price: 9800,
    description: '막 시작한, 혹은 성장 중인 셀러를 위한 플랜',
    features: getPlanFeatures('standard'),
    bgColorClass: 'bg-blue-50',
    borderColorClass: 'border-blue-500',
    textColorClass: 'text-blue-800',
    badgeBgColorClass: 'bg-blue-200',
    icon: <Zap className="w-6 h-6 text-blue-500" />
  },
  {
    id: 'premium',
    name: '프리미엄 플랜',
    price: 17800,
    description: '중대형 프로셀러를 위한 무제한 플랜',
    features: getPlanFeatures('premium'),
    recommended: true,
    bgColorClass: 'bg-purple-50',
    borderColorClass: 'border-purple-300',
    textColorClass: 'text-purple-800',
    badgeBgColorClass: 'bg-purple-200',
    icon: <LucideSparkles className="w-6 h-6 text-purple-500" />
  },
];

interface PlanCardProps {
  plan: Plan;
}

function PlanCard({ plan }: PlanCardProps) {
  const dailyPrice = plan.price > 0 ? Math.round(plan.price / 30) : 0;

  return (
    <Card
      className={`flex flex-col ${plan.borderColorClass} ${plan.bgColorClass} transition-all duration-300 hover:shadow-xl rounded-lg overflow-hidden relative p-6`}
    >
      {/* Step 6: "가장 인기" badge for premium */}
      {plan.recommended && (
        <div className="absolute top-0 right-0 bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
          BEST
        </div>
      )}
      <CardHeader className="p-0 pb-4 space-y-2 items-center text-center">
        <div className="flex items-center justify-center mb-3">
          {plan.icon}
        </div>
        <div className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-sm font-medium ${plan.badgeBgColorClass} ${plan.textColorClass} mx-auto`}>
          {plan.name}
        </div>
        <CardTitle className={`text-2xl sm:text-3xl font-bold mt-2 ${plan.textColorClass}`}>
          {plan.originalPrice && plan.discountRate ? (
            <div>
              <div className="flex items-center justify-center mb-0.5">
                <span className="text-xs sm:text-sm text-gray-500 line-through">
                  ₩{plan.originalPrice.toLocaleString()}
                </span>
                <span className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] sm:text-xs font-semibold ${plan.id === 'standard' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                  }`}>
                  {plan.discountRate}% 할인
                </span>
              </div>
              <div className="flex items-baseline justify-center">
                <span>₩{(plan.price || 0).toLocaleString()}</span>
                <span className="text-xs sm:text-sm text-gray-500 font-normal ml-1">/월</span>
              </div>
              {dailyPrice > 0 && (
                <div className="text-center text-xs text-gray-500 mt-0.5">
                  (하루에 약 <span className={`font-bold ${plan.id === 'standard' ? 'text-blue-600' : 'text-purple-600'}`}>{dailyPrice.toLocaleString()}원</span>)
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="flex items-baseline justify-center">
                <span>₩{(plan.price || 0).toLocaleString()}</span>
                <span className="text-xs sm:text-sm text-gray-500 font-normal ml-1">/월</span>
              </div>
              {dailyPrice > 0 && (
                <div className="text-center text-xs text-gray-500 mt-0.5">
                  (하루 <span className={`font-bold ${plan.id === 'standard' ? 'text-blue-600' : 'text-purple-600'}`}>{dailyPrice.toLocaleString()}원</span>)
                </div>
              )}
            </div>
          )}
        </CardTitle>
        {/* Step 9: fixed height → min-h */}
        <p className="text-xs sm:text-sm text-gray-500 min-h-[2.5rem] mb-4">{plan.description}</p>
      </CardHeader>
      <CardContent className="flex-grow p-0 pt-2">
        <ul className="space-y-2">
          {plan.features?.map((feature, index) => (
            <li key={index} className="flex items-start">
              <CheckCircle className={`h-4 w-4 mr-2 ${plan.id === 'standard'
                ? 'text-blue-500'
                : 'text-purple-500'
                } mt-0.5 flex-shrink-0`} />
              <span className="text-xs sm:text-sm text-gray-700 leading-relaxed">{feature}</span>
            </li>
          ))}
        </ul>
      </CardContent>
      {/* CTA button - outside CardContent so it sticks to bottom */}
      <Button
        asChild
        className={`w-full mt-6 ${plan.id === 'premium'
          ? 'bg-purple-600 hover:bg-purple-700 text-white'
          : 'bg-blue-600 hover:bg-blue-700 text-white'
          } transition-colors`}
      >
        <Link to="/order">7일 무료 체험 시작</Link>
      </Button>
    </Card>
  );
}
